import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Users, Briefcase, Clock, AlertCircle, CheckCircle2, Calendar, UserX, Flag,
  TrendingUp, Activity, Layers, GraduationCap, Hammer, ArrowUpRight, Percent, ArrowRight, Timer,
} from 'lucide-react';
import { api } from '../lib/api';
import { fmtDate } from '../lib/date';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import ProjektKalender from '../components/ProjektKalender';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [warnings, setWarnings] = useState(null);
  const [stats, setStats] = useState(null);
  const [projekte, setProjekte] = useState([]);
  const [kostenstellen, setKostenstellen] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date();

  useEffect(() => {
    const load = async () => {
      try {
        const [wRes, sRes, pRes, kRes] = await Promise.all([
          api.get('/warnings'),
          api.get('/dashboard/stats'),
          // Für den Projektkalender. Archivierte kommen mit und werden dort
          // gefiltert – so bleibt die Entscheidung an einer Stelle.
          api.get('/projects', { params: { include_archived: true } }),
          api.get('/cost-centers'),
        ]);
        setWarnings(wRes.data);
        setStats(sRes.data);
        setProjekte(pRes.data || []);
        setKostenstellen(kRes.data || []);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading || !warnings || !stats) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-28 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <Skeleton className="h-28"/><Skeleton className="h-28"/><Skeleton className="h-28"/><Skeleton className="h-28"/>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Skeleton className="h-64"/><Skeleton className="h-64"/><Skeleton className="h-64"/>
        </div>
      </div>
    );
  }

  const conflictCount = (warnings.over_hours?.length || warnings.double_bookings?.length || 0) + (warnings.absence_conflicts?.length || 0);
  const budgetOverCount = warnings.over_budget_projects?.length || 0;
  const kpi = stats.kpis;

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-mono text-slate-500 uppercase tracking-wider">Heute – {format(today, 'EEEE', { locale: de })}</div>
            <div className="text-2xl md:text-3xl font-bold font-display mt-1">{fmtDate(today)}</div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {conflictCount > 0 && (
              <div className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 bg-rose-700 text-white">
                <AlertCircle size={14}/><span className="text-xs font-bold uppercase tracking-wider">Einsatzkonflikte</span>
                <span className="font-mono font-bold">{conflictCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Users size={16}/>} label="Mitarbeiter aktiv" value={kpi.active_employees} accent="slate" dataTestId="kpi-active-employees"/>
        <Kpi icon={<Briefcase size={16}/>} label="Projekte aktiv" value={kpi.active_projects} accent="slate" dataTestId="kpi-active-projects"/>
        <Kpi icon={<CheckCircle2 size={16}/>} label="Heute verplant" value={kpi.today_assigned} secondary={`${kpi.today_absent} abwesend`} accent="emerald" dataTestId="kpi-today-assigned"/>
        <Kpi icon={<Activity size={16}/>} label="Einsätze diese Woche" value={kpi.week_assignments} secondary={`${kpi.month_assignments} diesen Monat`} accent="sky" dataTestId="kpi-week-assignments"/>
      </div>

      {/* Second row of KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<ArrowUpRight size={16}/>} label="Kst-übergreifend (Woche)" value={kpi.cross_kst_this_week} accent="violet"/>
        <Kpi icon={<Hammer size={16}/>} label="Schabos-Einsätze (Woche)" value={kpi.schabos_this_week} accent="purple"/>
        <Kpi icon={<Calendar size={16}/>} label="Überfällige Projekte" value={warnings.overdue_projects?.length || 0} accent={(warnings.overdue_projects?.length || 0) > 0 ? 'rose' : 'slate'}/>
        <Kpi icon={<Clock size={16}/>} label="Läuft in ≤7 Tagen ab" value={warnings.upcoming_end_projects?.length || 0} accent={(warnings.upcoming_end_projects?.length || 0) > 0 ? 'amber' : 'slate'}/>
      </div>

      {/* Warnings */}
      {(conflictCount > 0 || budgetOverCount > 0 || warnings.overdue_projects?.length || warnings.upcoming_end_projects?.length ||
        warnings.unassigned_today?.length || warnings.expiring_contracts?.length ||
        warnings.non_workday_assignments?.length) ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display">
              <AlertTriangle size={18} className="text-amber-600" />Warnungen & Hinweise
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {warnings.over_hours?.length > 0 && (
              <WarningGroup level="critical" title={`Über ${warnings.daily_hours_soft_limit ?? 8}h/Tag`} icon={<Clock size={16}/>} dataTestId="warn-over-hours">
                {warnings.over_hours.map((d, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge className="bg-rose-700 hover:bg-rose-700 text-white">{d.employee_name}</Badge>
                    <span className="text-slate-700">am {fmtDate(d.date)}</span>
                    <span className="text-slate-800 text-xs font-mono font-bold">{d.total_hours}h</span>
                    <span className="text-slate-500 text-xs">– {d.project_names.join(', ')}</span>
                  </li>
                ))}
              </WarningGroup>
            )}
            {/* Legacy 'double_bookings' – nur anzeigen wenn es sich vom over_hours-Set unterscheidet (keine over_hours-Warnung) */}
            {(!warnings.over_hours?.length) && warnings.double_bookings?.length > 0 && (
              <WarningGroup level="critical" title="Doppelbuchungen" icon={<AlertCircle size={16}/>} dataTestId="warn-double-bookings">
                {warnings.double_bookings.map((d, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge className="bg-rose-700 hover:bg-rose-700 text-white">{d.employee_name}</Badge>
                    <span className="text-slate-700">am {fmtDate(d.date)}</span>
                    <span className="text-slate-500 text-xs font-mono">{d.count}×</span>
                    <span className="text-slate-500 text-xs">– {d.project_names.join(', ')}</span>
                  </li>
                ))}
              </WarningGroup>
            )}
            {warnings.over_budget_projects?.length > 0 && (
              <WarningGroup level="critical" title="Projekte über Budget" icon={<Timer size={16}/>} dataTestId="warn-over-budget">
                {warnings.over_budget_projects.map((d, i) => (
                  <li key={i} className="text-sm">
                    <button onClick={() => navigate(`/projekte/${d.project_id}`)} className="hover:underline flex flex-wrap items-center gap-2">
                      {d.project_number && <Badge variant="outline" className="font-mono text-[10px]">#{d.project_number}</Badge>}
                      <span className="font-semibold">{d.name}</span>
                      <span className="text-slate-600 text-xs">– {d.consumed_hours}h / {d.budget_hours}h ({d.utilization_pct}%)</span>
                      <Badge className="bg-rose-700 hover:bg-rose-700 text-white text-[10px]">+{d.over_by}h</Badge>
                    </button>
                  </li>
                ))}
              </WarningGroup>
            )}
            {warnings.absence_conflicts?.length > 0 && (
              <WarningGroup level="critical" title="Abwesenheitskonflikte" icon={<UserX size={16}/>}>
                {warnings.absence_conflicts.map((d, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge className="bg-rose-700 hover:bg-rose-700 text-white">{d.employee_name}</Badge>
                    <span>{d.absence_type} am {fmtDate(d.date)}, aber zugewiesen zu "{d.project_name}"</span>
                  </li>
                ))}
              </WarningGroup>
            )}
            {/* Teilzeit: eingeplant an einem Tag, an dem laut Richtlinie nicht
                gearbeitet wird. Kein Fehler – jemand kann einspringen –, aber
                die Stunden laufen dann gegen ein Soll von null. */}
            {warnings.non_workday_assignments?.length > 0 && (
              <WarningGroup level="notice" title="Eingeplant an einem freien Tag" icon={<Users size={16}/>} dataTestId="warn-non-workday">
                {warnings.non_workday_assignments.map((d, i) => (
                  <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge variant="outline" className="bg-white">{d.employee_name}</Badge>
                    <span className="text-slate-700">am {fmtDate(d.date)} für "{d.project_name}"</span>
                    <span className="text-slate-500 text-xs">– arbeitet an diesem Wochentag laut Richtlinie nicht</span>
                  </li>
                ))}
              </WarningGroup>
            )}
            {warnings.overdue_projects?.length > 0 && (
              <WarningGroup level="notice" title="Überfällige Projekte" icon={<Flag size={16}/>}>
                {warnings.overdue_projects.map((d, i) => (
                  <li key={i}>
                    <button onClick={() => navigate(`/projekte/${d.project_id}`)} className="text-left hover:underline text-sm">
                      <span className="font-semibold">{d.name}</span> – seit <span className="font-mono">{d.days_overdue}</span> Tagen überfällig
                    </button>
                  </li>
                ))}
              </WarningGroup>
            )}
            {warnings.upcoming_end_projects?.length > 0 && (
              <WarningGroup level="notice" title="Projekte laufen bald ab" icon={<Clock size={16}/>}>
                {warnings.upcoming_end_projects.map((d, i) => (
                  <li key={i}>
                    <button onClick={() => navigate(`/projekte/${d.project_id}`)} className="text-left hover:underline text-sm">
                      <span className="font-semibold">{d.name}</span> – noch <span className="font-mono">{d.days_remaining}</span> Tage
                    </button>
                  </li>
                ))}
              </WarningGroup>
            )}
            {warnings.unassigned_today?.length > 0 && (
              <WarningGroup level="notice" title="Heute nicht eingeplant" icon={<Users size={16}/>}>
                <li className="flex flex-wrap gap-1.5">
                  {warnings.unassigned_today.map((e) => (
                    <Badge key={e.employee_id} variant="outline" className="bg-white">{e.employee_name} ({e.type})</Badge>
                  ))}
                </li>
              </WarningGroup>
            )}
            {warnings.expiring_contracts?.length > 0 && (
              <WarningGroup level="info" title="Verträge laufen bald ab" icon={<Calendar size={16}/>}>
                {warnings.expiring_contracts.map((d, i) => (
                  <li key={i} className="text-sm">
                    <Badge variant="outline">{d.employee_name}</Badge> <span className="text-slate-600">({d.type})</span> – bis {fmtDate(d.end_date)} – <span className="font-mono text-slate-500">{d.days_remaining} Tage</span>
                  </li>
                ))}
              </WarningGroup>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-6 flex items-center gap-3 text-emerald-700">
            <CheckCircle2 size={20}/><span className="font-semibold">Alles in Ordnung – keine offenen Warnungen.</span>
          </CardContent>
        </Card>
      )}

      {/* Weekly Utilization Chart + Kst Capacity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display"><TrendingUp size={18}/>Auslastung diese Woche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-40">
              {stats.daily_utilization.map(d => {
                const h = Math.max(3, Math.min(100, d.utilization_pct));
                const isToday = d.date === stats.date;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end min-w-[40px] h-full">
                    <div className="text-[10px] font-mono text-slate-500 mb-1">{d.utilization_pct}%</div>
                    <div
                      className={`w-full rounded-t transition-all ${isToday ? 'bg-slate-900' : 'bg-slate-400'}`}
                      style={{ height: `${Math.round((h / 100) * 110)}px`, minHeight: '4px' }}
                      title={`${d.assigned} von ${d.available} MA verplant`}
                    />
                    <div className={`text-[10px] font-bold mt-1 ${isToday ? 'text-slate-900' : 'text-slate-500'}`}>
                      {d.day_label}
                    </div>
                    <div className="text-[9px] text-slate-400 font-mono">{format(new Date(d.date), 'dd.MM', { locale: de })}</div>
                  </div>
                );
              })}
            </div>
            <div className="text-xs text-slate-500 mt-3">Prozent der verfügbaren Mitarbeiter die an diesem Tag einem Projekt zugewiesen sind.</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display"><Users size={18}/>Mitarbeiter-Aufteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(stats.employee_types).filter(([_,v]) => v > 0).map(([k,v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className="text-xs w-24 text-slate-600">{k}</div>
                  <div className="flex-1 bg-slate-100 rounded h-5 overflow-hidden relative">
                    <div className="absolute inset-y-0 left-0 bg-slate-800" style={{ width: `${(v / kpi.active_employees) * 100}%` }}/>
                    <div className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-white mix-blend-difference">{v}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t mt-4 pt-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">Projekt-Status</div>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(stats.project_statuses).map(([k,v]) => (
                  <div key={k} className="flex items-center justify-between rounded border border-slate-200 px-2 py-1">
                    <span className="text-xs text-slate-600">{k}</span>
                    <span className="font-mono font-bold text-sm">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KSt Capacity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base font-display"><Layers size={18}/>Kapazität je Kostenstelle (Woche)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.kst_stats.map(c => (
              <div key={c.cost_center_id} className="rounded-md border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-3 h-3 rounded-sm" style={{ background: c.color }}></span>
                  <div className="font-semibold text-sm truncate flex-1">{c.cost_center_name}</div>
                  <Badge variant="outline" className="text-[10px]">{c.type}</Badge>
                </div>
                <div className="flex items-baseline gap-1.5 mb-1">
                  <span className="text-2xl font-bold font-mono">{c.utilization_pct}</span>
                  <span className="text-slate-500 text-sm"><Percent size={11} className="inline"/></span>
                  <span className="text-xs text-slate-500 ml-auto">{c.assignments_this_week} Einsätze</span>
                </div>
                <div className="text-[10px] text-slate-500">{c.employees_total} Mitarbeiter</div>
                <Progress value={c.utilization_pct} className="h-1.5 mt-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Project progress + Absence distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display"><Briefcase size={18}/>Projekt-Fortschritt</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.project_progress.length === 0 ? (
              <div className="text-sm text-slate-500">Keine aktiven Projekte.</div>
            ) : (
              <div className="space-y-2.5">
                {stats.project_progress.slice(0, 8).map(p => {
                  const kst = stats.kst_stats.find(k => k.cost_center_id === p.cost_center_id);
                  return (
                    <div key={p.id} className="rounded-md border border-slate-200 p-2.5 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/projekte/${p.id}`)} data-testid={`dashboard-project-${p.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: kst?.color || '#64748b' }}/>
                        {p.project_number && <span className="text-[10px] font-mono font-bold text-slate-500">#{p.project_number}</span>}
                        <div className="font-semibold text-sm truncate flex-1">{p.name}</div>
                        {p.delay_days > 0 && <Badge variant="outline" className="text-[10px]"><Clock size={10} className="mr-0.5"/>+{p.delay_days}T</Badge>}
                        {p.is_overdue && <Badge className="bg-rose-700 hover:bg-rose-700 text-white text-[10px]"><AlertTriangle size={10} className="mr-0.5"/>überfällig</Badge>}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-100 rounded h-2 overflow-hidden relative">
                          <div className={`absolute inset-y-0 left-0 ${p.is_overdue ? 'bg-rose-600' : p.progress_pct > 90 ? 'bg-amber-500' : 'bg-slate-700'}`} style={{ width: `${Math.min(p.progress_pct, 100)}%` }}/>
                        </div>
                        <div className="text-[10px] font-mono font-semibold text-slate-600 w-10 text-right">{p.progress_pct}%</div>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1 font-mono flex items-center gap-1">
                        {fmtDate(p.start_date)} <ArrowRight size={9}/> {fmtDate(p.current_end_date)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-display"><Calendar size={18}/>Abwesenheiten (Woche)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {Object.entries(stats.absence_types_this_week).length === 0 ? (
                <div className="text-sm text-slate-500">Keine Abwesenheiten diese Woche.</div>
              ) : (
                Object.entries(stats.absence_types_this_week).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between rounded border border-slate-200 px-2.5 py-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      {type === 'Schule' ? <GraduationCap size={13} className="text-amber-600"/> :
                       type === 'Urlaub' ? <Calendar size={13} className="text-sky-600"/> :
                       type === 'Krankheit' ? <UserX size={13} className="text-rose-600"/> :
                       <Calendar size={13} className="text-slate-500"/>}
                      <span className="font-medium">{type}</span>
                    </div>
                    <span className="font-mono font-bold text-sm">{count}</span>
                  </div>
                ))
              )}
            </div>
            {stats.upcoming_contracts.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><Clock size={12}/>Verträge enden (30 Tage)</div>
                <div className="space-y-1">
                  {stats.upcoming_contracts.map(c => (
                    <div key={c.employee_id} className="flex items-center justify-between text-xs">
                      <span className="font-medium truncate">{c.employee_name}</span>
                      <span className="font-mono text-slate-500">{c.days_remaining}T</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Projektzeiträume je Kostenstelle, wochenweise.
          Steht bewusst am Ende: Es ist eine Übersicht zum Nachsehen, keine
          Meldung, die Handeln verlangt. Oben gehören die Zahlen und die
          Warnungen hin – was dringend ist, darf nicht nach unten rutschen. */}
      <ProjektKalender projects={projekte} costCenters={kostenstellen} />
    </div>
  );
}

function Kpi({ icon, label, value, secondary, accent = 'slate', dataTestId }) {
  const bg = {
    slate: 'border-slate-200 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50',
    sky: 'border-sky-200 bg-sky-50',
    violet: 'border-violet-200 bg-violet-50',
    purple: 'border-purple-200 bg-purple-50',
    rose: 'border-rose-300 bg-rose-50',
    amber: 'border-amber-300 bg-amber-50',
  }[accent];
  const iconBg = {
    slate: 'bg-slate-900 text-white',
    emerald: 'bg-emerald-600 text-white',
    sky: 'bg-sky-600 text-white',
    violet: 'bg-violet-600 text-white',
    purple: 'bg-purple-700 text-white',
    rose: 'bg-rose-700 text-white',
    amber: 'bg-amber-600 text-white',
  }[accent];
  return (
    <div className={`rounded-lg border p-3 ${bg}`} data-testid={dataTestId}>
      <div className="flex items-start gap-2">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${iconBg}`}>{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight truncate">{label}</div>
          <div className="text-2xl font-bold font-mono text-slate-900 leading-none mt-1">{value}</div>
          {secondary && <div className="text-[10px] text-slate-500 mt-1">{secondary}</div>}
        </div>
      </div>
    </div>
  );
}

function WarningGroup({ level, title, icon, children, dataTestId }) {
  const levelCls = {
    critical: 'border-rose-300 bg-rose-50',
    notice: 'border-amber-300 bg-amber-50',
    info: 'border-slate-200 bg-slate-50',
  }[level];
  const badgeCls = {
    critical: 'bg-rose-700 text-white',
    notice: 'bg-amber-600 text-white',
    info: 'bg-slate-600 text-white',
  }[level];
  return (
    <div className={`rounded-md border ${levelCls} p-3`} data-testid={dataTestId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs font-bold ${badgeCls}`}>
          {icon}
          {title}
        </span>
      </div>
      <ul className="space-y-1.5">{children}</ul>
    </div>
  );
}
